/** @format */

import { Chain, Environment, ProductSkuCatalogDetails } from '@shared/data-access/interfaces';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class CartEngineDao {
	constructor(
		private readonly http: HttpClient,
		private readonly environment: Environment,
		private readonly chain: Chain
	) {}

	private DEFAULT_HEADERS = {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'Access-Control-Allow-Origin': '*',
		'Access-Control-Allow-Methods': 'GET,POST,OPTIONS,PUT',
		'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
		Pragma: 'no-cache',
		Expires: 'Sat, 01 Jan 2000 00:00:00 GMT'
	};

	// HTTP Headers
	private HTTP_HEADERS = new HttpHeaders(this.DEFAULT_HEADERS);

	public static handleError(error: HttpErrorResponse | any) {
		// return an ErrorObservable
		return throwError(error);
	}

	public getCart$() {
		const url = '/api/v1/carts';
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.get(url, httpOptions).pipe(
			map((getCartResponse: any) => getCartResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	/**
	 * Deletes the cart by making an HTTP DELETE request to the server.
	 *
	 * @returns {Observable<void>} An observable that completes when the cart is successfully deleted.
	 * @throws {HttpErrorResponse} If the server returns an error or a network issue occurs.
	 */
	public deleteCart$() {
		const url = '/api/v1/carts';
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.delete<void>(url, httpOptions).pipe(catchError(CartEngineDao.handleError));
	}

	public putContentsSku$(sku: string) {
		const url = `/api/v1/carts/contents/${sku}?qty=1`;
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.put(url, httpOptions).pipe(
			map((putContentsSkuResponse: any) => putContentsSkuResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	public deleteContentsSku$(deleteSku: string) {
		const url = `/api/v1/carts/contents/${deleteSku}`;
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.delete(url, httpOptions).pipe(
			map((deleteContentsSkuResponse: any) => deleteContentsSkuResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	public getContentsCount$() {
		const url = '/api/v1/carts/contents/count';
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.get(url, httpOptions).pipe(
			map((getContentsCountResponse: any) => getContentsCountResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	public postContentsSkuQty$(postSku: string, postQty: string) {
		const url = `/api/v1/carts/contents/${postSku}/qty/${postQty}`;
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.post(url, null, httpOptions).pipe(
			map((postContentsSkuQtyResponse: any) => postContentsSkuQtyResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	public postContentsSkuProcure$(
		sku: string,
		newProcureMethod: string,
		storeNumber: string,
		zipCode?: string
	) {
		let url = `/api/v1/carts/contents/${sku}/procure`;

		if (newProcureMethod === 'BOPIS') {
			url += `?bopis=true&store=${storeNumber}`;
		}

		if (newProcureMethod === 'BOPL') {
			url += `?bopl=true&store=${storeNumber}`;
		}

		if (newProcureMethod === 'SAMEDAY') {
			url += `?sdd=true&zipcode=${zipCode}`;
		}

		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.post(url, null, httpOptions).pipe(
			map((postContentsSkuProcureResponse: any) => postContentsSkuProcureResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	public postContentsAllProcure$(newProcureMethod: string, storeNumber: string) {
		let url = `/api/v1/carts/contents/procure`;

		if (newProcureMethod === 'BOPIS') {
			url += `?bopis=true&store=${storeNumber}`;
		}

		if (newProcureMethod === 'BOPL') {
			url += `?bopl=true&store=${storeNumber}`;
		}
		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		httpOptions.headers = httpOptions.headers.set('Content-Length', '0'); // Default to 0 in case users browser doesn't provide content-length (All major browsers typically do)

		return this.http.patch(url, {}, httpOptions).pipe(
			map((postContentsSkuProcureResponse: any) => postContentsSkuProcureResponse),
			catchError(CartEngineDao.handleError)
		);
	}

	public postContentsSkuAddOn$(
		warrantySku: number,
		parentSku: string,
		qty: number,
		type: string
	): Observable<any> {
		const url = `/api/v1/carts/contents/${parentSku}/addOn`;

		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http
			.post(url, { sku: warrantySku, qty, type }, httpOptions)
			.pipe(catchError(CartEngineDao.handleError));
	}

	public deleteContentsSkuAddOn$(sku: number, type = 'warranty'): Observable<any> {
		const url = `/api/v1/carts/contents/${sku}/addOn/${type}`;

		const httpOptions = {
			headers: this.HTTP_HEADERS,
			withCredentials: true
		};

		return this.http.delete(url, httpOptions).pipe(catchError(CartEngineDao.handleError));
	}

	public getProductDetailsBySkuV4$(skus: string): Observable<ProductSkuCatalogDetails[]> {
		let params = new HttpParams();
		params = params.set('id', skus);

		const httpOptions = {
			params,
			withCredentials: false
		};

		return this.http
			.get<
				ProductSkuCatalogDetails[]
			>(`${this.environment.api.searchProduct.origin}/catalog-productdetails/v4/bySku/${this.chain.storeId}`, httpOptions)
			.pipe(catchError(CartEngineDao.handleError));
	}
}
